import * as React from "react";
import { useNavigate } from "react-router";

import { useDeleteAgent } from "@/shared/domain/agents/hooks";
import { Card, RobotoButton } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { DeleteAgentDialog } from "./DeleteAgentDialog";
import styles from "./sections.module.css";

export interface DangerZoneProps {
  agentId: string;
  /** The org that owns the agent, which the delete is addressed to. */
  orgId: string;
  /** What the person types to confirm. */
  agentName: string;
}

/** Deleting the agent, behind a dialog that asks for its name. */
export function DangerZone({ agentId, orgId, agentName }: DangerZoneProps) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const { mutate: deleteAgent, isPending, error, reset } = useDeleteAgent();

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      reset();
    }
  };

  const handleDelete = () => {
    deleteAgent(
      { orgId, agentId },
      {
        // In the same tick as success: `useDeleteAgent` has just dropped this
        // agent from the cache, and a page still mounted a render later would
        // fetch it again and flash a not-found error.
        onSuccess: () => {
          setOpen(false);
          void navigate(Goto.route.Agents);
        },
      },
    );
  };

  return (
    <Card.Root size="section" data-testid="agent-detail-danger-zone">
      <Card.Header>
        <Card.Title>Danger zone</Card.Title>
      </Card.Header>
      <Card.Body>
        <div className={styles.row}>
          <div className={styles["row-text"]}>
            <span className={styles["row-title"]}>Delete this agent</span>
            <p className={styles["row-summary"]}>
              Its threads are kept, but triggers that launch it will fail until
              they are pointed at another agent.
            </p>
          </div>
          <RobotoButton
            variant="destructive"
            className={styles["row-action"]}
            onClick={() => setOpen(true)}
            data-testid="agent-delete"
          >
            Delete agent
          </RobotoButton>
        </div>
        <DeleteAgentDialog
          open={open}
          onOpenChange={handleOpenChange}
          resourceName={agentName}
          onDelete={handleDelete}
          isDeleting={isPending}
          errorMessage={
            error === null
              ? undefined
              : error.message || "Something went wrong deleting this agent."
          }
        />
      </Card.Body>
    </Card.Root>
  );
}
